import { ApolloProvider } from '@apollo/client';
import {
  isBuiltInLabelCategory,
  isLabelContentType,
  SiteUrlsContext,
  useRenderLabels,
  UseRenderLabelsParams } from '@tableau/catalog-ui-temp';
import React from 'react';

import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { useSelector } from '~src/settings/general-settings/utils';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { catalogApolloClient } from './CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from './CatalogFeatureFlagsAdapter';
import { catalogQueryClient } from './CatalogProviders';
import { CERTIFIED_INDICATOR_TYPE, SENSITIVITY_INDICATOR_TYPE, WARNING_INDICATOR_TYPE } from './components/labels/DataQualityType';
import { urlForContentType } from './components/listView/DataQualityIndicatorCell';
import { getTelemetryCommonProperties } from './DataCatalogTelemetry';

type LabelWrapperProps = {
  readonly itemInfo: IContentInfo<'virtualConnection' | 'database' | 'datasource' | 'flow' | 'virtualConnectionTable' | 'table' | 'workbook'>;
  readonly category?: 'warning' | 'certification' | 'sensitivity' | 'custom';
  readonly kind: 'chip' | 'icon';
  readonly isEager: boolean;
  readonly wrapper?: (e: JSX.Element) => JSX.Element;
}

export function LabelWrapper(props: LabelWrapperProps): JSX.Element {
  const urls = useSelector(siteUrlsSelector);
  const wrapper = props.wrapper ?? ((e: JSX.Element) => e);

  return(
    <SiteUrlsContext.Provider value={urls}>
      <ApolloProvider client={catalogApolloClient}>
        {wrapper(useRenderLabelsVpc(props))}
      </ApolloProvider>
    </SiteUrlsContext.Provider>
  );
}

function useRenderLabelsVpc({ itemInfo, category, kind, isEager }: {
  readonly itemInfo: IContentInfo<'virtualConnection' | 'database' | 'datasource' | 'flow' | 'virtualConnectionTable' | 'table' | 'workbook'>;
  readonly category?: 'warning' | 'certification' | 'sensitivity' | 'custom';
  readonly kind: 'chip' | 'icon';
  readonly isEager: boolean;
}): JSX.Element {
  const catalogEnabled = useSelector(SessionSiteSelectors.catalogingOnSiteEnabled);
  const urls = useSelector(siteUrlsSelector);
  const params: UseRenderLabelsParams = {
    catalogEnabled,
    content : {
      contentId: itemInfo.id,
      contentLuid: itemInfo.luid,
      contentType: isLabelContentType(itemInfo.contentType) ? itemInfo.contentType : undefined,
      contentName: itemInfo.name,
      contentUrl: urlForContentType(urls, itemInfo),
      subjectArea: itemInfo.contentType,
    },
    includeUpstream: true,
    includeParented: false,
    displayCategories: category? getDisplayCategories(category) : undefined,
    probablyOrEager: buildProbablyOrEager({isEager, category, itemInfo}),
    testId: itemInfo.contentType + '-testId',
    controls:{
      kind,
      siteUrls: urls,
    }
  };

  return useRenderLabelsWithContext(params);
}


function useRenderLabelsWithContext({ ...params }: UseRenderLabelsParams): JSX.Element | null {
  const siteLuid = useSelector(SessionSiteSelectors.siteLuid);
  const urls = useSelector(siteUrlsSelector);
  const [telemetryContext] = React.useState(() => getTelemetryCommonProperties());

  return useRenderLabels ({
    ...params,
    contextOverrides: {
      queryClient: catalogQueryClient,
      restApiServiceParams: { siteLuid },
      siteUrls: urls,
      telemetryContext,
      featureFlags: catalogFeatureFlagsAdapter(),
    },
  });
}

function buildProbablyOrEager({ isEager, category, itemInfo }: {
  readonly itemInfo: IContentInfo<'virtualConnection' | 'database' | 'datasource' | 'flow' | 'virtualConnectionTable' | 'table'| 'workbook'>;
  readonly category?: 'warning' | 'certification' | 'sensitivity'| 'custom';
  readonly isEager: boolean;
}): UseRenderLabelsParams['probablyOrEager'] {
  if (isEager) {
    return 'eager';
  }
  const activeLabels = itemInfo.dataQualityIndicators?.filter(t => t.isActive) ?? [];
  if (activeLabels.length === 0) {
    return {};
  }

  const warnings = activeLabels.filter(t => t.type === WARNING_INDICATOR_TYPE);
  const warningsCount = warnings.length;
  const hasSevereWarning = warnings.some(t => t.isSevere);
  const sensitivities = activeLabels.filter(t => t.type === SENSITIVITY_INDICATOR_TYPE);
  const sensitivityCount = sensitivities.length;
  const hasSevereSensitivity = sensitivities.some(t => t.isSevere);
  const certifications = activeLabels.filter(t => t.type === CERTIFIED_INDICATOR_TYPE);
  const certificationCount = certifications.length;

  switch (category) {
    case 'certification': return {
      certified: certificationCount > 0
    };
    case 'warning':
      return {
        warning: warningsCount > 0 ? {
          value: 'Warning',
          severe: hasSevereWarning,
          count: warningsCount,
        } : undefined,
      };
    case 'sensitivity':
      return {
        sensitivity: sensitivityCount > 0 ? {
          value: 'Sensitive data',
          severe: hasSevereSensitivity,
          count: sensitivityCount
        }: undefined
      };
    case 'custom': return null;
    default: {
      return {
        certified: certificationCount > 0,
        warning: warningsCount > 0 ? {
          value: 'Warning',
          severe: hasSevereWarning,
          count: warningsCount,
        } : undefined,
        sensitivity: sensitivityCount > 0 ? {
          value: 'Sensitive data',
          severe: hasSevereSensitivity,
          count: sensitivityCount
        }: undefined
      };
    }
  }
}

function getDisplayCategories(category: 'warning' | 'certification' | 'sensitivity' | 'custom'){
  return {
    certification: category === 'certification' ? true : undefined,
    warning: category === 'warning' ? true : undefined,
    sensitivity: category === 'sensitivity' ? true : undefined,
    custom: !(isBuiltInLabelCategory(category)) ? true : undefined
  };
}
